import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setIsVerifying(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
        setTokenValid(res.data.valid);
        setTokenEmail(res.data.email || '');
      } catch {
        setTokenValid(false);
      } finally {
        setIsVerifying(false);
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (isVerifying) {
      return (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-[#0066CC] mx-auto mb-4" />
          <p className="text-white/50">Verifying your reset link...</p>
        </div>
      );
    }
    if (!token || !tokenValid) {
      return (
        <div className="text-center py-4" data-testid="invalid-token-message">
          <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="font-bold text-white text-lg mb-2">Invalid or Expired Link</h3>
          <p className="text-white/40 text-sm mb-6">This reset link is no longer valid. Please request a new one.</p>
          <Link to="/forgot-password">
            <Button className="bg-[#0066CC] hover:bg-[#0055aa] rounded-xl h-11 px-6" data-testid="request-new-link-btn">
              Request New Reset Link
            </Button>
          </Link>
        </div>
      );
    }
    if (success) {
      return (
        <div className="text-center py-4" data-testid="password-reset-success">
          <div className="w-16 h-16 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h3 className="font-bold text-white text-lg mb-2">Password Reset Complete</h3>
          <p className="text-white/40 text-sm mb-6">You can now sign in with your new password.</p>
          <Button onClick={() => navigate('/login')} className="bg-[#0066CC] hover:bg-[#0055aa] rounded-xl h-11 px-6" data-testid="go-to-login-btn">
            Go to Sign In
          </Button>
        </div>
      );
    }

    return (
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
        {tokenEmail && (
          <div className="bg-[#0066CC]/10 border border-[#0066CC]/20 rounded-xl px-4 py-2.5">
            <p className="text-sm text-blue-300">Resetting for <span className="text-white font-medium">{tokenEmail}</span></p>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-white/50 text-xs font-semibold uppercase tracking-wider">New Password</Label>
          <div className="relative group">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-white/20 group-focus-within:text-[#0066CC] transition-colors" />
            <Input id="password" type="password" placeholder="Min 6 characters" value={password} onChange={(e) => setPassword(e.target.value)}
              className="pl-11 h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#0066CC]/50 focus:ring-2 focus:ring-[#0066CC]/10 rounded-xl"
              required minLength={6} data-testid="new-password-input" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/50 text-xs font-semibold uppercase tracking-wider">Confirm Password</Label>
          <div className="relative group">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-white/20 group-focus-within:text-[#0066CC] transition-colors" />
            <Input id="confirm" type="password" placeholder="Re-enter password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-11 h-12 bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/20 focus:border-[#0066CC]/50 focus:ring-2 focus:ring-[#0066CC]/10 rounded-xl"
              required data-testid="confirm-password-input" />
          </div>
        </div>
        <Button type="submit" className="w-full h-12 bg-[#0066CC] hover:bg-[#0055aa] text-white font-semibold rounded-xl shadow-lg shadow-[#0066CC]/20" disabled={isLoading} data-testid="reset-password-submit-btn">
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
        </Button>
      </form>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#050a18]" />
      <div className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] bg-[#0066CC]/15 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-15%] left-[-5%] w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[100px]" />
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="w-full max-w-[420px] px-6 relative z-10">
        {/* Logo */}
        <div className="text-center mb-10">
          <img src="/absolin-logo-hd.png" alt="ABSOLIN" className="h-10 w-auto mx-auto mb-2" />
          <p className="text-white/30 text-[11px] tracking-[0.2em] uppercase">Infinite Digital Possibilities</p>
        </div>

        {/* Card */}
        <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 shadow-2xl">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white">Reset Password</h2>
            <p className="text-white/40 text-sm mt-1">Set your new password below</p>
          </div>
          {renderContent()}
        </div>

        <div className="mt-6 text-center flex items-center justify-center gap-4">
          <Link to="/login" className="inline-flex items-center text-white/30 hover:text-white/60 text-sm transition-colors" data-testid="back-to-login-reset">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Sign In
          </Link>
          <span className="text-white/15">·</span>
          <Link to="/" className="inline-flex items-center text-white/30 hover:text-white/60 text-sm transition-colors" data-testid="back-to-website-reset">
            Visit website
          </Link>
        </div>
      </div>
    </div>
  );
}
